/**
 * Persona verdicts for a capability — what each credential template (a
 * "persona", managed on the Test Users tab) can/cannot/limited-do with this
 * capability. Read is used in `CapabilityDrawer`'s Personas section; the two
 * write hooks back the same section's per-persona editor (set a verdict,
 * remove one — a removal tombstones the row via `suppressedAt` so an agent
 * never resurrects a human's removal).
 *
 * @module features/product-map/useCapabilityPersonas
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

export type CapabilityPersonaAccess = 'can' | 'cannot' | 'limited';

export const PERSONA_ACCESS_OPTIONS: Array<{ id: CapabilityPersonaAccess; label: string }> = [
  { id: 'can', label: 'Can' },
  { id: 'cannot', label: 'Cannot' },
  { id: 'limited', label: 'Limited' },
];

export const PERSONA_ACCESS_VARIANT: Record<CapabilityPersonaAccess, 'success' | 'destructive' | 'warning'> = {
  can: 'success',
  cannot: 'destructive',
  limited: 'warning',
};

/** A recorded persona verdict — present only for personas that have one. */
export interface CapabilityPersonaView {
  id: string;
  orgId: string;
  capabilityId: string;
  credentialTemplateId: string;
  credentialTemplateName: string;
  access: CapabilityPersonaAccess;
  note: string | null;
  source: 'user' | 'agent';
  createdBy: string | null;
  suppressedAt: string | null;
}

export function useCapabilityPersonas(capabilityId: string | undefined) {
  return useQuery({
    queryKey: ['capability-personas', capabilityId],
    enabled: Boolean(capabilityId),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/product-map/capabilities/${capabilityId}/personas`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch personas');
      const json = (await res.json()) as { data?: CapabilityPersonaView[] };
      return json.data ?? [];
    },
  });
}

export function useSetCapabilityPersona(capabilityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { templateId: string; access: CapabilityPersonaAccess; note?: string | null }) => {
      const res = await fetch(`${API_URL}/product-map/capabilities/${capabilityId}/personas/${input.templateId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ access: input.access, note: input.note ?? null }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to set persona access');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['capability-personas', capabilityId] }),
  });
}

export function useRemoveCapabilityPersona(capabilityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      const res = await fetch(`${API_URL}/product-map/capabilities/${capabilityId}/personas/${templateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to remove persona');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['capability-personas', capabilityId] }),
  });
}
